<template>
  <div v-if="isOpen" class="fixed inset-0 z-50 overflow-y-auto">
    <div class="fixed inset-0 bg-black opacity-50" @click="close"></div>

    <div class="flex items-center justify-center min-h-screen p-4">
      <div class="bg-white rounded-lg shadow-xl w-full max-w-md mx-auto z-10">
        <div class="px-6 py-4 border-b">
          <h2 class="text-xl font-semibold text-gray-800">Записаться на прием</h2>
        </div>

        <div class="p-6">
          <div v-if="loading" class="flex justify-center py-8">
            <div class="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-600"></div>
          </div>

          <div v-else-if="error" class="bg-red-50 p-4 rounded-md text-red-600 mb-4">
            {{ $errorMessage(error) }}
          </div>

          <div v-else-if="showPaymentState">
            <div class="mb-6 bg-gray-50 p-4 rounded-lg">
              <h3 class="font-medium mb-2">Информация о враче</h3>
              <p><span class="text-gray-600">ФИО:</span> {{ doctor.full_name }}</p>
              <p v-if="formattedDoctorSpecialization">
                <span class="text-gray-600">Специализация:</span> {{ formattedDoctorSpecialization }}
              </p>
            </div>

            <div v-if="isPaymentPaidState" class="bg-green-50 p-4 rounded-md text-green-700 mb-4">
              <div class="font-medium mb-1">Оплата получена</div>
              <div>Запись подтверждена. Мы закроем окно автоматически.</div>
            </div>

            <div v-else-if="isPaymentExpiredState" class="bg-red-50 p-4 rounded-md text-red-700 mb-4">
              <div class="font-medium mb-1">Время на оплату истекло</div>
              <div>Бронь была снята автоматически. Пожалуйста, выберите новое время приема.</div>
              <button
                class="mt-3 px-3 py-2 text-sm bg-white border border-red-200 rounded-md hover:bg-red-50"
                @click="returnToBookingForm"
              >
                Выбрать другое время
              </button>
            </div>

            <div v-else class="space-y-4">
              <div class="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div class="font-semibold text-amber-900 mb-1">Оплатите прием через Kaspi</div>
                <p class="text-sm text-amber-800">
                  Бронь удерживается до указанного времени. После успешной оплаты запись подтвердится автоматически.
                </p>
              </div>

              <div class="bg-slate-50 rounded-lg p-4 space-y-3">
                <div>
                  <div class="text-xs uppercase tracking-wide text-slate-500">Номер записи</div>
                  <div class="font-mono text-sm font-semibold text-slate-900 break-all">
                    {{ paymentInfo.payment_account }}
                  </div>
                </div>
                <div>
                  <div class="text-xs uppercase tracking-wide text-slate-500">Сумма</div>
                  <div class="text-lg font-semibold text-slate-900">
                    {{ formattedPaymentAmount }}
                  </div>
                </div>
                <div>
                  <div class="text-xs uppercase tracking-wide text-slate-500">Оплатить до</div>
                  <div class="text-sm text-slate-900">
                    {{ formattedPaymentDeadline }}
                  </div>
                </div>
                <div>
                  <div class="text-xs uppercase tracking-wide text-slate-500">Осталось времени</div>
                  <div class="text-sm font-medium" :class="paymentCountdownClass">
                    {{ paymentCountdownText }}
                  </div>
                </div>
                <div>
                  <div class="text-xs uppercase tracking-wide text-slate-500">Статус</div>
                  <div class="text-sm text-slate-900">
                    {{ paymentStatusText }}
                  </div>
                </div>
                <div v-if="selectedService" class="text-sm text-slate-700">
                  <span class="text-slate-500">Услуга:</span> {{ selectedService.name }}
                </div>
                <div v-if="selectedSlot" class="text-sm text-slate-700">
                  <span class="text-slate-500">Прием:</span>
                  {{ formatDate(selectedSlot.date) }}, {{ formatTime(selectedSlot.start_time) }} - {{ formatTime(selectedSlot.end_time) }}
                </div>
              </div>

              <div class="bg-blue-50 rounded-lg p-4 text-sm text-blue-700">
                <div class="font-medium mb-1">Как оплатить</div>
                <div>На вебе можно сгенерировать QR для оплаты в приложении Kaspi. При необходимости остается ручная оплата по номеру записи.</div>
              </div>

              <div v-if="quickPayQrSrc" class="bg-white border border-slate-200 rounded-lg p-4">
                <div class="text-sm font-medium text-slate-900 mb-3">QR для оплаты в Kaspi</div>
                <div class="flex justify-center">
                  <img
                    :src="quickPayQrSrc"
                    alt="QR для оплаты в Kaspi"
                    class="w-56 h-56 rounded-lg border border-slate-200 bg-white p-2"
                  >
                </div>
                <div class="mt-3 text-xs text-slate-500 text-center">
                  Откройте приложение Kaspi и отсканируйте QR-код
                </div>
              </div>

              <button
                class="w-full px-4 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
                :disabled="loading || quickPayLoading"
                @click="startKaspiQuickPay"
              >
                {{ quickPayLoading ? 'Готовим оплату в Kaspi...' : (quickPayQrSrc ? 'Обновить QR Kaspi' : 'Показать QR Kaspi') }}
              </button>

              <button
                v-if="quickPayRedirectUrl"
                class="w-full px-4 py-3 bg-white border border-emerald-200 text-emerald-700 rounded-lg hover:bg-emerald-50 disabled:opacity-50 disabled:cursor-not-allowed"
                :disabled="loading || quickPayLoading"
                @click="openKaspiQuickPayRedirect"
              >
                Открыть Kaspi
              </button>

              <div v-if="paymentQuickPayError" class="bg-red-50 rounded-lg p-3 text-sm text-red-600">
                {{ $errorMessage(paymentQuickPayError) }}
              </div>

              <div v-if="paymentPollingError" class="bg-red-50 rounded-lg p-3 text-sm text-red-600">
                {{ $errorMessage(paymentPollingError) }}
              </div>
            </div>
          </div>

          <div v-else>
            <div class="mb-6 bg-gray-50 p-4 rounded-lg">
              <h3 class="font-medium mb-2">Информация о враче</h3>
              <p><span class="text-gray-600">ФИО:</span> {{ doctor.full_name }}</p>
              <p v-if="formattedDoctorSpecialization">
                <span class="text-gray-600">Специализация:</span> {{ formattedDoctorSpecialization }}
              </p>
              <p><span class="text-gray-600">Стаж:</span> {{ doctor.experience_years }} лет</p>
            </div>

            <div v-if="doctor.offline_appointments_enabled" class="mb-4">
              <label class="block text-sm font-medium text-gray-700 mb-2">Формат приема</label>
              <div class="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  class="rounded-md border px-3 py-2 text-sm font-medium"
                  :class="appointmentFormat === 'online' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'"
                  @click="appointmentFormat = 'online'"
                >Онлайн</button>
                <button
                  type="button"
                  class="rounded-md border px-3 py-2 text-sm font-medium"
                  :class="appointmentFormat === 'offline' ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'"
                  @click="appointmentFormat = 'offline'"
                >В клинике</button>
              </div>
            </div>

            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700 mb-1">Выберите дату</label>
              <input
                v-model="selectedDate"
                type="date"
                class="form-input w-full rounded-md border-gray-300 shadow-sm"
                :min="minDate"
                :max="maxDate"
                @change="handleDateChange"
              >
            </div>

            <div v-if="selectedDate && availableSlots.length > 0" class="mb-6">
              <label class="block text-sm font-medium text-gray-700 mb-2">Доступное время</label>
              <div class="grid grid-cols-3 gap-2">
                <button
                  v-for="slot in availableSlots"
                  :key="slot.id"
                  class="py-2 px-3 border rounded-md text-center"
                  :class="selectedSlot && selectedSlot.id === slot.id ? 'bg-blue-100 border-blue-400 text-blue-800' : 'hover:bg-gray-50'"
                  @click="selectSlot(slot)"
                >
                  {{ formatTime(slot.start_time) }}
                </button>
              </div>
            </div>

            <div v-else-if="selectedDate" class="text-center py-4 text-gray-600">
              <p>На выбранную дату нет доступных слотов</p>
              <p class="mt-2 text-sm">Пожалуйста, выберите другую дату или обратитесь в клинику напрямую</p>
              <button
                class="mt-3 px-3 py-1 text-blue-600 border border-blue-300 rounded-md hover:bg-blue-50"
                @click="handleDateChange"
              >
                Попробовать загрузить ещё раз
              </button>
            </div>

            <div v-if="selectedSlot" class="mb-6 mt-2 p-3 bg-blue-50 rounded-md text-blue-800">
              <p class="font-medium">Выбранное время:</p>
              <p>{{ formatDate(selectedSlot.date) }}, {{ formatTime(selectedSlot.start_time) }} - {{ formatTime(selectedSlot.end_time) }}</p>
            </div>

            <div v-if="isCommercialBooking" class="mb-4">
              <label class="mb-1 block text-sm font-medium text-gray-700" for="patient-booking-service">Услуга <span class="text-red-500">*</span></label>
              <select id="patient-booking-service" v-model="selectedServiceId" class="w-full rounded-md border border-gray-300 px-3 py-2" :disabled="loadingReasons"><option :value="null">Выберите услугу</option><option v-for="service in bookingServices" :key="service.service_id" :value="service.service_id">{{ service.name }} · {{ formatMoney(appointmentFormat === 'offline' ? service.offline_price : service.price) }}</option></select>
            </div>

            <div class="mb-6">
              <label class="block text-sm font-medium text-gray-700 mb-1">
                Причина визита <span class="text-red-500">*</span>
              </label>

              <div v-if="loadingReasons" class="flex items-center py-2 text-gray-500">
                <svg class="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Загрузка причин визита...
              </div>

              <div v-else>
                <select
                  v-model="selectedReasonId"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 mb-3"
                  required
                >
                  <option value="">-- Выберите причину визита --</option>
                  <option v-for="reason in visitReasons" :key="reason.id" :value="reason.id">
                    {{ reason.name }}
                  </option>
                </select>

                <div v-if="selectedReason && selectedReason.description" class="mb-3 p-2 bg-blue-50 rounded-md text-sm text-blue-700">
                  {{ selectedReason.description }}
                </div>

                <div v-if="selectedReasonPrice" class="mb-3 p-2 bg-amber-50 rounded-md text-sm text-amber-800">
                  Стоимость услуги: {{ selectedReasonPrice }}
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">
                    Дополнительные детали (необязательно)
                  </label>
                  <textarea
                    v-model="reasonText"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    rows="3"
                    placeholder="Добавьте дополнительные детали к причине визита"
                  ></textarea>
                </div>
              </div>
            </div>

            <div v-if="bookingSuccess" class="bg-green-50 p-4 rounded-md text-green-600 mb-4">
              Вы успешно записаны на прием!
            </div>
          </div>
        </div>

        <div class="px-6 py-3 border-t bg-gray-50 flex justify-between rounded-b-lg">
          <button
            class="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
            @click="close"
          >
            Закрыть
          </button>

          <button
            v-if="showPaymentState && isPaymentAwaiting"
            class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            :disabled="loading"
            @click="refreshPaymentStatus()"
          >
            Проверить оплату
          </button>

          <button
            v-else-if="!showPaymentState"
            class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            :disabled="!canBook || loading || bookingSuccess"
            :class="{ 'opacity-50 cursor-not-allowed': !canBook || loading || bookingSuccess }"
            @click="bookAppointment"
          >
            {{ bookingSuccess ? 'Записано' : 'Записаться' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '../services/api'
import { formatDoctorSpecialization } from '../utils/doctorDisplay'

export default {
  name: 'AppointmentBooking',
  props: {
    isOpen: {
      type: Boolean,
      required: true
    },
    doctor: {
      type: Object,
      required: true,
      default: () => ({})
    }
  },
  data() {
    return {
      loading: false,
      error: null,
      selectedDate: null,
      selectedSlot: null,
      availableSlots: [],
      bookingSuccess: false,
      loadingReasons: false,
      selectedReasonId: null,
      selectedServiceId: null, bookingServices: [],
      reasonText: '',
      appointmentFormat: 'online',
      visitReasons: [],
      createdAppointmentId: null,
      paymentInfo: null,
      paymentPollTimer: null,
      paymentCloseTimer: null,
      paymentClockTimer: null,
      paymentPollingError: null,
      quickPaySession: null,
      paymentQuickPayError: null,
      quickPayLoading: false,
      nowTs: Date.now()
    }
  },
  computed: {
    minDate() {
      const today = new Date()
      return today.toISOString().split('T')[0]
    },
    maxDate() {
      const maxDate = new Date()
      maxDate.setDate(maxDate.getDate() + 30)
      return maxDate.toISOString().split('T')[0]
    },
    canBook() {
      return this.selectedSlot && this.selectedReasonId && (!this.isCommercialBooking || this.selectedServiceId)
    },
    isCommercialBooking() { return Boolean(this.doctor.clinic_is_commercial || this.doctor.clinic?.is_commercial || this.doctor.clinic?.clinic_type === 'commercial') },
    selectedService() { return this.bookingServices.find(service => service.service_id === this.selectedServiceId) || null },
    selectedReason() {
      return this.visitReasons.find(reason => String(reason.id) === String(this.selectedReasonId)) || null
    },
    selectedReasonPrice() {
      if (!this.selectedService) return null
      return this.formatMoney(this.appointmentFormat === 'offline' ? this.selectedService.offline_price : this.selectedService.price)
    },
    formattedDoctorSpecialization() {
      return formatDoctorSpecialization(this.doctor)
    },
    showPaymentState() {
      return Boolean(this.paymentInfo && this.paymentInfo.payment_required)
    },
    isPaymentAwaiting() {
      return this.showPaymentState && this.paymentInfo.payment_status === 'pending'
    },
    isPaymentPaidState() {
      return this.showPaymentState && this.paymentInfo.payment_status === 'paid'
    },
    isPaymentExpiredState() {
      return this.showPaymentState && ['expired', 'cancelled', 'failed'].includes(this.paymentInfo.payment_status)
    },
    formattedPaymentAmount() {
      if (!this.paymentInfo || !this.paymentInfo.payment_amount) return '—'
      return this.formatMoney(this.paymentInfo.payment_amount)
    },
    formattedPaymentDeadline() {
      if (!this.paymentInfo || !this.paymentInfo.payment_expires_at) return '—'
      return this.formatDateTime(this.paymentInfo.payment_expires_at)
    },
    paymentCountdownSeconds() {
      if (!this.paymentInfo || !this.paymentInfo.payment_expires_at) return 0
      const expiresAt = new Date(this.paymentInfo.payment_expires_at).getTime()
      return Math.max(0, Math.floor((expiresAt - this.nowTs) / 1000))
    },
    paymentCountdownText() {
      if (this.isPaymentExpiredState) return '00:00'
      const totalSeconds = this.paymentCountdownSeconds
      const minutes = String(Math.floor(totalSeconds / 60)).padStart(2, '0')
      const seconds = String(totalSeconds % 60).padStart(2, '0')
      return `${minutes}:${seconds}`
    },
    paymentCountdownClass() {
      return this.paymentCountdownSeconds <= 120 ? 'text-red-600' : 'text-amber-700'
    },
    paymentStatusText() {
      const statusMap = {
        pending: 'Ожидает оплаты',
        paid: 'Оплачено',
        expired: 'Время оплаты истекло',
        cancelled: 'Платеж отменен',
        failed: 'Ошибка оплаты'
      }
      return statusMap[this.paymentInfo?.payment_status] || 'Неизвестно'
    },
    quickPayQrSrc() {
      const rawValue = this.quickPaySession?.qr_code_image
      if (!rawValue) return null
      if (String(rawValue).startsWith('data:')) {
        return rawValue
      }
      return `data:image/png;base64,${rawValue}`
    },
    quickPayRedirectUrl() {
      return this.quickPaySession?.redirect_url || null
    }
  },
  watch: {
    appointmentFormat() { if (this.isOpen) { this.selectedServiceId = null; this.loadVisitReasons() } },
    isOpen(value) {
      if (value) {
        this.reset()
        this.loadInitialSlots()
        this.loadVisitReasons()
      } else {
        this.stopPaymentTracking()
      }
    }
  },
  methods: {
    close() {
      this.stopPaymentTracking()
      if (this.createdAppointmentId) {
        this.$emit('appointment-created')
      }
      this.$emit('close')
    },
    reset() {
      this.stopPaymentTracking()
      this.selectedDate = null
      this.selectedSlot = null
      this.availableSlots = []
      this.error = null
      this.bookingSuccess = false
      this.loadingReasons = false
      this.selectedReasonId = null
      this.selectedServiceId = null; this.bookingServices = []
      this.reasonText = ''
      this.appointmentFormat = 'online'
      this.visitReasons = []
      this.createdAppointmentId = null
      this.paymentInfo = null
      this.paymentPollingError = null
      this.quickPaySession = null
      this.paymentQuickPayError = null
      this.quickPayLoading = false
      this.nowTs = Date.now()
    },
    returnToBookingForm() {
      this.stopPaymentTracking()
      this.createdAppointmentId = null
      this.paymentInfo = null
      this.paymentPollingError = null
      this.quickPaySession = null
      this.paymentQuickPayError = null
      this.quickPayLoading = false
      this.bookingSuccess = false
      this.nowTs = Date.now()
      this.loadAvailableSlots()
    },
    stopPaymentTracking() {
      if (this.paymentPollTimer) {
        clearInterval(this.paymentPollTimer)
        this.paymentPollTimer = null
      }
      if (this.paymentCloseTimer) {
        clearTimeout(this.paymentCloseTimer)
        this.paymentCloseTimer = null
      }
      if (this.paymentClockTimer) {
        clearInterval(this.paymentClockTimer)
        this.paymentClockTimer = null
      }
    },
    scheduleCloseAfterSuccess() {
      if (this.paymentCloseTimer) {
        clearTimeout(this.paymentCloseTimer)
      }
      this.paymentCloseTimer = setTimeout(() => {
        this.close()
      }, 2000)
    },
    startPaymentTracking() {
      if (!this.createdAppointmentId || !this.showPaymentState) return
      this.stopPaymentTracking()
      this.paymentClockTimer = setInterval(() => {
        this.nowTs = Date.now()
      }, 1000)
      this.paymentPollTimer = setInterval(() => {
        this.refreshPaymentStatus({ silent: true })
      }, 5000)
    },
    applyPaymentState(payload) {
      this.paymentInfo = payload
      this.paymentPollingError = null
      this.paymentQuickPayError = null
      if (payload.payment_status !== 'pending') {
        this.quickPaySession = null
      }
      this.nowTs = Date.now()

      if (payload.payment_status === 'paid') {
        this.bookingSuccess = true
        this.stopPaymentTracking()
        this.scheduleCloseAfterSuccess()
        return
      }

      if (['expired', 'cancelled', 'failed'].includes(payload.payment_status)) {
        this.stopPaymentTracking()
        this.loadAvailableSlots()
        return
      }

      this.bookingSuccess = false
      this.startPaymentTracking()
    },
    async refreshPaymentStatus({ silent = false } = {}) {
      if (!this.createdAppointmentId) return
      if (!silent) {
        this.loading = true
      }
      try {
        const response = await api.getAppointmentPayment(this.createdAppointmentId)
        this.applyPaymentState(response.data || {})
      } catch (pollError) {
        this.paymentPollingError = this.$t('errors.paymentStatus')
      } finally {
        if (!silent) {
          this.loading = false
        }
      }
    },
    submitKaspiQuickPayForm(payload) {
      const actionUrl = payload?.action_url
      const method = (payload?.method || 'POST').toUpperCase()
      const formFields = payload?.form_fields || {}

      if (!actionUrl || !Object.keys(formFields).length) {
        throw new Error('Не удалось подготовить форму оплаты Kaspi.')
      }

      const form = document.createElement('form')
      form.method = method
      form.action = actionUrl
      form.style.display = 'none'
      form.acceptCharset = 'UTF-8'

      Object.entries(formFields).forEach(([name, value]) => {
        const input = document.createElement('input')
        input.type = 'hidden'
        input.name = name
        input.value = value === null || value === undefined ? '' : String(value)
        form.appendChild(input)
      })

      document.body.appendChild(form)
      form.submit()
      document.body.removeChild(form)
    },
    openKaspiQuickPayRedirect() {
      if (!this.quickPayRedirectUrl) return
      window.open(this.quickPayRedirectUrl, '_blank', 'noopener')
    },
    async startKaspiQuickPay() {
      if (!this.createdAppointmentId || !this.isPaymentAwaiting) return

      this.quickPayLoading = true
      this.paymentQuickPayError = null
      try {
        const response = await api.createKaspiQuickPay(this.createdAppointmentId, { mode: 'qr' })
        const payload = response.data || {}
        if (payload.mode === 'qr_json') {
          this.quickPaySession = payload
        } else {
          this.quickPaySession = null
          this.submitKaspiQuickPayForm(payload)
        }
      } catch (quickPayError) {
        this.paymentQuickPayError = this.$apiError(quickPayError, this.$t('errors.kaspiRedirect'))
      } finally {
        this.quickPayLoading = false
      }
    },
    async loadInitialSlots() {
      this.selectedDate = new Date().toISOString().split('T')[0]
      await this.loadAvailableSlots()
    },
    async handleDateChange() {
      this.selectedSlot = null
      await this.loadAvailableSlots()
    },
    async loadAvailableSlots() {
      if (!this.selectedDate) return

      this.loading = true
      this.error = null

      try {
        const formattedDate = this.selectedDate
        const response = await api.getAvailableSlots(this.doctor.id, formattedDate, formattedDate)
        const slotsData = response.data.results || response.data

        if (!Array.isArray(slotsData)) {
          this.availableSlots = []
          return
        }

        this.availableSlots = slotsData.filter(slot => {
          const slotDate = new Date(slot.date).toISOString().split('T')[0]
          return (
            (!this.doctor.id || slot.doctor == this.doctor.id) &&
            !slot.is_booked &&
            slot.is_booking_open !== false &&
            slotDate === formattedDate
          )
        })

        if (this.availableSlots.length === 0) {
          const altResponse = await api.getDoctorAvailableSlots(this.doctor.id)
          const altSlotsData = altResponse.data.results || altResponse.data

          if (Array.isArray(altSlotsData)) {
            this.availableSlots = altSlotsData.filter(slot => {
              const slotDate = new Date(slot.date).toISOString().split('T')[0]
              return !slot.is_booked && slot.is_booking_open !== false && slotDate === formattedDate
            })
          }
        }

        this.availableSlots.sort((a, b) => a.start_time.localeCompare(b.start_time))
      } catch (loadError) {
        this.error = this.$t('errors.loadSlots')
      } finally {
        this.loading = false
      }
    },
    selectSlot(slot) {
      this.selectedSlot = slot
    },
    formatTime(timeString) {
      return timeString ? timeString.substring(0, 5) : '—'
    },
    formatDate(dateString) {
      if (!dateString) return '—'
      const date = new Date(dateString)
      return date.toLocaleDateString()
    },
    formatDateTime(dateTimeString) {
      if (!dateTimeString) return '—'
      const date = new Date(dateTimeString)
      return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
    },
    formatMoney(value) {
      const amount = Number(value || 0)
      return `${new Intl.NumberFormat('ru-RU', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      }).format(amount)} ₸`
    },
    async bookAppointment() {
      if (!this.canBook) return

      this.loading = true
      this.error = null

      try {
        const response = await api.createAppointment({
          doctor: this.doctor.id,
          slot: this.selectedSlot.id,
          reason: this.selectedReasonId,
          service_id: this.selectedServiceId || undefined,
          reason_text: this.reasonText,
          appointment_format: this.appointmentFormat
        })

        const appointment = response.data || {}
        this.createdAppointmentId = appointment.id || null

        if (appointment.payment_required) {
          this.applyPaymentState(appointment)
          return
        }

        this.bookingSuccess = true
        this.scheduleCloseAfterSuccess()
      } catch (bookingError) {
        if (bookingError.response && bookingError.response.data && bookingError.response.data.slot) {
          this.error = this.$t('errors.slotTaken')
          await this.loadAvailableSlots()
        } else if (bookingError.response?.data?.detail) {
          this.error = this.$apiError(bookingError)
        } else {
          this.error = this.$t('errors.bookAppointment')
        }
      } finally {
        this.loading = false
      }
    },
    async loadVisitReasons() {
      this.loadingReasons = true
      try {
        const format = this.appointmentFormat
        const [response, services] = await Promise.all([api.getVisitReasons({ doctor: this.doctor.id, appointment_format: format }), api.getBookingServices({ doctor: this.doctor.id, appointment_format: format })])
        if (format !== this.appointmentFormat) return
        this.visitReasons = response.data || []
        this.bookingServices = services.data || []
        if (this.bookingServices.length === 1) this.selectedServiceId = this.bookingServices[0].service_id
      } catch (loadError) {
        this.error = this.$t('errors.loadVisitReasons')
      } finally {
        this.loadingReasons = false
      }
    }
  },
  beforeUnmount() {
    this.stopPaymentTracking()
  }
}
</script>

<style scoped>
.form-input,
.form-textarea {
  @apply focus:ring-blue-500 focus:border-blue-500;
}
</style>
